import { Autocomplete, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import AmountField from "../../../../../common/components/functional/AmountField";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import { selectActiveWorkLocations } from "../../../../customer/customerWorkLocationsSlice";
import {
  fetchOrderTemplates,
  selectActiveOrderTemplates,
} from "../../../../customer/orderTemplatesSlice";
import { FormTitleGrid } from "../../../styles";
import { OrderRowTemplate } from "../../../../../common/types/apiTypes";
import { selectOrderRowById } from "../../../orderRowsDetailedSlice";
import { useLocation, useParams } from "react-router-dom";
import { format } from "date-fns";
import { dateTimeFormat } from "../../../../../common/helpers/formatDateTime";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import { OrderTemplate, WorkLocation } from "../../../orderTypes";
import { TargetedShifts } from "./TargetedShifts";
import { NameIdOption } from "../../../../../common/types/commonTypes";

type Props = {
  updateCostCenters: (id: string) => void;
  customerOrg: NameIdOption;
  customerCompany: NameIdOption;
};

export const BasicDetails = ({
  updateCostCenters,
  customerOrg,
  customerCompany,
}: Props) => {
  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();
  const localized = useAppSelector((state) => state.localized);
  const { bundleId } = useParams();
  const { pathname } = useLocation();

  const { control, watch, setValue } = useFormContext();

  const workLocations = useAppSelector(selectActiveWorkLocations);
  const orderRowTemplates = useAppSelector(selectActiveOrderTemplates);
  const isCopying = pathname.includes("copy");

  const { workLocation, edit, isReadOnly } = watch();
  const workTitleId = watch("orderTemplate.workTitleId");
  const contractId = watch("orderTemplate.contractId");

  const order = useAppSelector((state) =>
    selectOrderRowById(state, bundleId!!),
  );
  const [maxAmount] = useState<number>(
    order && !isCopying ? order.amount : 250,
  );

  // Update/prefill default requirements and variations when order template is changed
  const updateDefaultRequirements = (orderTemplate: OrderRowTemplate) => {
    if (orderTemplate) {
      setValue("tesVariation", orderTemplate.tesVariation);
      setValue(
        "skills",
        orderTemplate.skills.map((id: string) => ({
          id,
          name: localized.skill[id],
        })),
      );
      setValue(
        "certificates",
        orderTemplate.certificates.map((id: string) => ({
          id,
          name: localized.certificate[id],
        })),
      );
      setValue(
        "trainings",
        orderTemplate.trainings.map((id: string) => ({
          id,
          name: localized.training[id],
        })),
      );
      setValue("languages", orderTemplate.languages);
    } else {
      setValue("tesVariation", []);
      setValue("skills", []);
      setValue("certificates", []);
      setValue("trainings", []);
      setValue("languages", []);
    }
  };

  const clearDescriptionFields = () => {
    setValue("workDescriptionFi", "");
    setValue("workDescriptionEn", "");
    setValue("arrivalInstructionsFi", "");
    setValue("arrivalInstructionsEn", "");
  };

  const onUpdateWorkTitle = (newValue: OrderTemplate) => {
    if (edit) return;
    if (newValue && workLocation) {
      setValue(
        "arrivalInstructionsFi",
        workLocation.descriptionForTalent || "",
      );
      setValue(
        "arrivalInstructionsEn",
        workLocation.descriptionForTalentEng || "",
      );
    }
    if (workLocation && newValue) {
      // set work description fields based on selected order template/work title
      setValue(
        "workDescriptionFi",
        workLocation.info[newValue.workTitleId]?.fi || "",
      );
      setValue(
        "workDescriptionEn",
        workLocation.info[newValue.workTitleId]?.en || "",
      );
    } else {
      clearDescriptionFields();
    }
  };

  const onUpdateWorkLocation = (newValue: WorkLocation) => {
    if (edit) return;
    setValue("orderTemplate", null);
    if (newValue && workTitleId) {
      setValue("workDescriptionFi", newValue.info[workTitleId]?.fi || "");
      setValue("workDescriptionEn", newValue.info[workTitleId]?.en || "");
    }
    if (newValue) {
      // set arrival instruction fields based on selected work location
      setValue("arrivalInstructionsFi", newValue.descriptionForTalent || "");
      setValue("arrivalInstructionsEn", newValue.descriptionForTalentEng || "");
    } else {
      clearDescriptionFields();
    }
  };

  useEffect(() => {
    if (workLocation?.contractId) {
      dispatch(fetchOrderTemplates({ contractId: workLocation.contractId }));
    }
  }, [workLocation?.contractId, dispatch]);

  useEffect(() => {
    if (workLocation?.id) {
      updateCostCenters(workLocation.id);
    }
  }, [workLocation?.id, updateCostCenters]);

  return (
    <FormSection title={t("orderDetails")}>
      {edit && (
        <>
          <FormTitleGrid container item xs={4}>
            <Typography variant="secondary">{t("orderName")}</Typography>
          </FormTitleGrid>
          <Grid item xs={6}>
            <CommonTextField
              disabled
              sx={{ maxWidth: "372px" }}
              fullWidth
              value={order?.title}
            />
          </Grid>
          <FormTitleGrid container item xs={4}>
            <Typography variant="secondary">{t("orderDate")}</Typography>
          </FormTitleGrid>
          <Grid item xs={6}>
            <CommonTextField
              disabled
              sx={{ maxWidth: "372px" }}
              fullWidth
              value={
                order?.timestampOrdered
                  ? format(new Date(order.timestampOrdered), dateTimeFormat)
                  : order?.dateOrdered
                    ? format(new Date(order?.dateOrdered), dateTimeFormat)
                    : ""
              }
            />
          </Grid>
          <FormTitleGrid container item xs={4}>
            <Typography variant="secondary">{t("orderer")}</Typography>
          </FormTitleGrid>
          <Grid item xs={6}>
            <CommonTextField
              disabled
              sx={{ maxWidth: "372px" }}
              fullWidth
              value={order?.orderer}
            />
          </Grid>
          <FormTitleGrid container item xs={4}>
            <Typography variant="secondary">{t("customer")}</Typography>
          </FormTitleGrid>
          <Grid item xs={6}>
            <CommonTextField
              disabled
              sx={{ maxWidth: "372px" }}
              fullWidth
              value={order?.customerOrgName}
            />
          </Grid>
        </>
      )}
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("workLocation")}</Typography>
      </FormTitleGrid>
      <Grid item xs={6}>
        <Controller
          name="workLocation"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              disabled={edit}
              value={field.value}
              options={workLocations.sort((a, b) =>
                a.name.localeCompare(b.name),
              )}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              onChange={(_, newValue) => {
                setValue("customerCostCenters", []);
                field.onChange(newValue);
                onUpdateWorkLocation(newValue);
              }}
              renderInput={(params) => (
                <CommonTextField {...params} sx={{ maxWidth: "372px" }} />
              )}
            />
          )}
        />
      </Grid>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("orderTemplate")}</Typography>
      </FormTitleGrid>
      <Grid item xs={6}>
        <Controller
          name="orderTemplate"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <>
              <Autocomplete
                disabled={edit}
                value={field.value}
                options={orderRowTemplates.sort((a, b) =>
                  a.name.localeCompare(b.name),
                )}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                  updateDefaultRequirements(newValue);
                  onUpdateWorkTitle(newValue);
                }}
                renderInput={(params) => (
                  <CommonTextField {...params} sx={{ maxWidth: "372px" }} />
                )}
              />
            </>
          )}
        />
      </Grid>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("amount")}</Typography>
      </FormTitleGrid>
      <Grid item xs={6}>
        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <AmountField
              sx={{ width: "140px" }}
              size="small"
              value={field.value}
              onChange={(value: number) => field.onChange(value)}
              min={1}
              max={maxAmount}
              disabled={isReadOnly}
            />
          )}
        />
      </Grid>
      <TargetedShifts
        customerOrg={customerOrg}
        customerCompany={customerCompany}
        contractId={order?.contractId || contractId || workLocation?.contractId}
      />
    </FormSection>
  );
};
